/**
 * Lenavio Learn API client.
 *
 * Mirror of ``backend/app/routers/training.py``. Single source of
 * truth for the types every Training page imports.
 */
import client from "./client";

// ── Course types ──────────────────────────────────────────────────

export type CourseCategory = "compliance" | "skills" | "onboarding" | "custom";
export type Recurrence = "one_time" | "annual" | "quarterly";
export type AssignmentStatus =
  | "assigned"
  | "in_progress"
  | "completed"
  | "overdue";

export interface TrainingCourse {
  id: string;
  org_id: string;
  title: string;
  description: string | null;
  category: CourseCategory;
  content_url: string | null;
  duration_minutes: number | null;
  is_required: boolean;
  recurrence: Recurrence;
  is_active: boolean;
  is_default_template: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  assignment_count: number;
  completion_count: number;
}

export interface CourseCreatePayload {
  title: string;
  description?: string;
  category?: CourseCategory;
  content_url?: string;
  duration_minutes?: number;
  is_required?: boolean;
  recurrence?: Recurrence;
}

export interface CourseUpdatePayload {
  title?: string;
  description?: string;
  category?: CourseCategory;
  content_url?: string;
  duration_minutes?: number;
  is_required?: boolean;
  recurrence?: Recurrence;
  is_active?: boolean;
}

// ── Assignment types ──────────────────────────────────────────────

export interface AssignmentCreatePayload {
  course_id: string;
  employee_ids?: string[];
  department_ids?: string[];
  all_employees?: boolean;
  due_date?: string; // YYYY-MM-DD
}

export interface BulkAssignResult {
  course_id: string;
  created: number;
  skipped_existing: number;
  target_employees: number;
}

export interface TrainingAssignment {
  id: string;
  org_id: string;
  course_id: string;
  employee_id: string;
  assigned_by: string | null;
  assigned_at: string;
  due_date: string | null;
  status: AssignmentStatus;
  completed_at: string | null;
  score: number | null;
  course_title: string | null;
  course_category: CourseCategory | null;
  course_duration_minutes: number | null;
  course_content_url: string | null;
}

export interface AssignmentCompletePayload {
  score?: number;
  certificate_url?: string;
}

// ── Dashboard / compliance ────────────────────────────────────────

export interface TrainingStatusBreakdown {
  assigned: number;
  in_progress: number;
  completed: number;
  overdue: number;
}

export interface DepartmentTrainingStats {
  department_id: string | null;
  department_name: string;
  employees: number;
  assignments: number;
  completed: number;
  overdue: number;
  completion_rate: number;
}

export interface TrainingDashboard {
  total_courses: number;
  active_assignments: number;
  completion_rate: number;
  overdue_count: number;
  breakdown: TrainingStatusBreakdown;
  departments: DepartmentTrainingStats[];
}

export interface ComplianceCourseStatus {
  course_id: string;
  title: string;
  recurrence: string;
  eligible_employees: number;
  completed: number;
  overdue: number;
  completion_rate: number;
  health: "green" | "yellow" | "red";
}

export interface ComplianceReport {
  courses: ComplianceCourseStatus[];
  overdue_employees: {
    employee_id: string;
    name: string;
    course_title: string;
    due_date: string | null;
  }[];
}

// ── Endpoints ─────────────────────────────────────────────────────

export async function listCourses(
  opts?: { include_inactive?: boolean; category?: string },
): Promise<TrainingCourse[]> {
  const res = await client.get<TrainingCourse[]>("/training/courses", {
    params: opts,
  });
  return res.data;
}

export async function getCourse(id: string): Promise<TrainingCourse> {
  const res = await client.get<TrainingCourse>(`/training/courses/${id}`);
  return res.data;
}

export async function createCourse(
  data: CourseCreatePayload,
): Promise<TrainingCourse> {
  const res = await client.post<TrainingCourse>("/training/courses", data);
  return res.data;
}

export async function updateCourse(
  id: string,
  data: CourseUpdatePayload,
): Promise<TrainingCourse> {
  const res = await client.patch<TrainingCourse>(`/training/courses/${id}`, data);
  return res.data;
}

export async function deleteCourse(id: string): Promise<void> {
  await client.delete(`/training/courses/${id}`);
}

export async function assignCourse(
  data: AssignmentCreatePayload,
): Promise<BulkAssignResult> {
  const res = await client.post<BulkAssignResult>("/training/assign", data);
  return res.data;
}

export async function completeAssignment(
  id: string,
  data: AssignmentCompletePayload = {},
): Promise<TrainingAssignment> {
  const res = await client.put<TrainingAssignment>(
    `/training/assignments/${id}/complete`,
    data,
  );
  return res.data;
}

export async function listMyCourses(
  includeCompleted = true,
): Promise<TrainingAssignment[]> {
  const res = await client.get<TrainingAssignment[]>("/training/my-courses", {
    params: { include_completed: includeCompleted },
  });
  return res.data;
}

export async function getDashboard(): Promise<TrainingDashboard> {
  const res = await client.get<TrainingDashboard>("/training/dashboard");
  return res.data;
}

export async function getComplianceReport(): Promise<ComplianceReport> {
  const res = await client.get<ComplianceReport>("/training/compliance-report");
  return res.data;
}
